import React, { useMemo, useCallback } from 'react';
import Cascader from '@intelligence/components/Form/components/common/Cascader';
import type { IChildProp, TCascaderValue } from '@intelligence/components/Form/type';
import useIntlExtend from '@/pages/intelligence/hooks/useIntlExtend';
import { getDataSourceIcon } from '@/pages/intelligence/utils/getDataSourceImg';

export interface ICascadeRankingProps {
  value: string[];
  onChange: (value: string[]) => void;
  options: IChildProp[];
  className?: string;
  style?: React.CSSProperties;
  /** 筛选器收起时显示的标题前缀，默认 "Ranking" */
  groupName?: string;
  /** 多选时显示的单位名，默认 "Ranking" */
  itemName?: string;
}

const CascadeRanking: React.FC<ICascadeRankingProps> = (props) => {
  const {
    value = [],
    onChange,
    options = [],
    className,
    style,
    groupName,
    itemName,
  } = props;

  const { formatMessage } = useIntlExtend();

  const finalGroupName = groupName || formatMessage({ id: 'dbi_ranking' });
  const finalItemName = itemName || formatMessage({ id: 'dbi_ranking' });

  /**
   * 标准化 options：为子节点挂载 parent 引用与 parentKey，保证取消勾选和清空操作正常定位
   */
  const normalizedOptions = useMemo<IChildProp[]>(() => {
    return options.map((parent) => {
      const parentGroup: IChildProp = {
        ...parent,
        children: [],
      };
      parentGroup.children = (parent.children || []).map((child) => ({
        ...child,
        parent: parentGroup,
        cascaderDataSourceValue: parent.value,
      }));
      return parentGroup;
    });
  }, [options]);

  // 将一维 string[] 转换为 Cascader 内部按组分类的 Record 结构
  const cascaderValue = useMemo(() => {
    const result: TCascaderValue = {};
    normalizedOptions.forEach((parent) => {
      const parentKey = parent.value as string;
      const childrenKeys = parent.children?.map(c => c.value as string) || [];
      result[parentKey] = childrenKeys.filter(k => value.includes(k));
    });
    return result;
  }, [value, normalizedOptions]);

  // 将 Cascader 内部 Record 结构拍平为一维 string[] 输出
  const handleChange = useCallback((_field: string, newValue: TCascaderValue) => {
    const flatArray: string[] = [];
    Object.values(newValue || {}).forEach((arr) => {
      if (Array.isArray(arr)) {
        flatArray.push(...arr);
      }
    });
    onChange?.(flatArray);
  }, [onChange]);

  /** 自定义筛选器按钮内容：全选显示 All，单选显示图标与名称，多选显示数量 */
  const customInputDisplayRender = useCallback(({
    DisplayEmpty,
  }: any) => {
    const allKeys = normalizedOptions.flatMap(p => p.children?.map(c => c.value) || []);
    const isAllSelected = allKeys.length > 0 && allKeys.every(k => value.includes(k as string));

    let contentUI: React.ReactNode = null;

    if (isAllSelected) {
      contentUI = <span>{formatMessage({ id: 'dbi_all' })}</span>;
    } else if (value.length === 1) {
      const selectedValue = value[0];
      const platform = normalizedOptions.find(p => p.children?.some(c => c.value === selectedValue));
      const leafItem = platform?.children?.find(c => c.value === selectedValue);
      const labelKey = leafItem?.labelKey || '';
      const text = labelKey ? formatMessage({ id: labelKey }) : (leafItem?.label || selectedValue);
      const platformIcon = platform?.icon ? getDataSourceIcon(platform.icon) : undefined;

      contentUI = (
        <div
          className="dbi-cascader-select-button-flex"
          style={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle', maxWidth: 'calc(100% - 60px)' }}
        >
          {platformIcon && (
            <img
              className="dbi-cascader-common-icon"
              src={platformIcon}
              alt=""
              style={{
                width: 16,
                height: 16,
                minWidth: 16,
                minHeight: 16,
                objectFit: 'contain',
                flexShrink: 0,
                marginRight: 4,
              }}
            />
          )}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {text}
          </span>
        </div>
      );
    } else if (value.length > 1) {
      contentUI = <span>{value.length}</span>;
    } else {
      contentUI = <DisplayEmpty />;
    }

    return (
      <div className="dbi-cascader-select-button dbi-select-button">
        <span className="select-button-text">{finalGroupName}</span>
        {contentUI}
      </div>
    );
  }, [normalizedOptions, formatMessage, finalGroupName, value]);

  /** 自定义已选 Tag 展示：根据平台拼接对应国际化文本 */
  const customSearchItemDisplayRender = useCallback(({ selectedItem, tagAutoWidth, tagWidth }: any) => {
    const platform = normalizedOptions.find(p => p.children?.some(c => c.value === selectedItem.value));
    let text = selectedItem.label || '';
    if (selectedItem.labelKey) {
      const prefixedKey = `${selectedItem.labelKey}_${platform?.value}`;
      const translated = formatMessage({ id: prefixedKey });
      text = translated === prefixedKey ? formatMessage({ id: selectedItem.labelKey }) : translated;
    }

    return (
      <span
        style={{
          maxWidth: tagAutoWidth ? 'unset' : tagWidth,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'inline-block',
          verticalAlign: 'middle',
        }}
      >
        {text}
      </span>
    );
  }, [normalizedOptions, formatMessage]);

  return (
    <div style={style} className={className}>
      <Cascader
        field="ranking"
        value={cascaderValue}
        options={normalizedOptions}
        onChange={handleChange}
        groupName={finalGroupName}
        itemName={finalItemName}
        mainCategory="dbi_platform"
        subCategory="dbi_ranking"
        hasSelectAll={true}
        hasSearch={true}
        searchPlaceholder={formatMessage({ id: 'dbi_search_ranking_here' })}
        searchTagAutoWidth={true}
        height={208}
        customInputDisplayRender={customInputDisplayRender}
        customSearchItemDisplayRender={customSearchItemDisplayRender}
      />
    </div>
  );
};

export default CascadeRanking;
